import {
  AttachFileOutlined,
  Cancel,
  FileDownload,
  FileUploadOutlined,
  SystemUpdateAltOutlined,
} from '@mui/icons-material'
import {
  Alert,
  Button,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material'
import {
  type Employee,
  type PlaceNode,
  PlaceNodeTypeEnum,
} from '@ulysses-inc/harami_api_client'
import { type FormikHelpers, useFormik } from 'formik'
import { parse, type ParseResult } from 'papaparse'
import type React from 'react'
import { type FC, useRef } from 'react'
import { CSVLink } from 'react-csv'
import { useDispatch, useSelector } from 'react-redux'
import FormikErrorMessage from 'src/components/formikErrorMessage/FormikErrorMessage'
import { getUniqueFlattenNodes } from 'src/exShared/util/place/getUniqueFlattenNodes'
import { flattenNodes } from 'src/exShared/util/place/placeNode'
import Yup from 'src/features/validation/yup'
import type { RootState } from 'src/state/store'
import Loading from '../../../components/loading/Loading'
import employeesOperations from '../../../state/ducks/employees/operations'

export type EmployeesForm = { employees: Employee[]; fileName: string }

const ImportEmployees: FC = () => {
  const dispatch = useDispatch()

  const { filter, isLoading, placeNodeWarning } = useSelector(
    (state: RootState) => state.employeesState.employees,
  )

  const nodes = useSelector(
    (state: RootState) => state.placesState.placeGroups.nodes,
  )
  const places = useSelector(
    (state: RootState) => state.placesState.places.places,
  )
  const placeNodes = getUniqueFlattenNodes([
    ...flattenNodes(nodes ?? []),
    ...(places ?? []),
  ])

  const updatePlaceNodeWarning = (message: string) => {
    employeesOperations.updatePlaceNodeWarning(dispatch, message)
  }

  const initialValues: EmployeesForm = { employees: [], fileName: '' }
  const validationSchema = Yup.object().shape({
    // 配列型である必要がある
    // 最小1つの要素が必要
    // 条件を満たさない場合、'CSVファイルに従業員が含まれていません'というエラーメッセージを表示
    employees: Yup.array().min(1, 'CSVファイルに従業員が含まれていません'),
    // 文字列型である必要がある
    // 必須項目（空欄不可）
    // エラーメッセージで表示される際のラベルは'CSVファイル'
    fileName: Yup.string().required().label('CSVファイル'),
  })
  const formik = useFormik({
    initialValues: initialValues,
    validationSchema,
    onSubmit: (
      values: EmployeesForm,
      formikHelpers: FormikHelpers<EmployeesForm>,
    ) => {
      const request = { limit: 25, offset: 0 }
      employeesOperations.addMultipleEmployees(
        dispatch,
        values.employees,
        request,
        filter,
      )
      formikHelpers.resetForm()
      updatePlaceNodeWarning('')
    },
    enableReinitialize: true,
  })

  const parseEmployeesFromFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files && e.target.files.item(0)
    formik.setFieldValue('fileName', file?.name ?? '')
    updatePlaceNodeWarning('')
    if (file) {
      if (!file.name.toLowerCase().endsWith('.csv')) {
        formik.setFieldTouched('employees', true, false)
        formik.setFieldError('employees', 'CSVファイルを選択してください')
        e.target.value = ''
        return
      }
      const reader = new FileReader()
      reader.onload = () => {
        const arrayBuffer = reader.result as ArrayBuffer
        let text: string
        try {
          const decoder = new TextDecoder('utf-8', { fatal: true })
          text = decoder.decode(arrayBuffer)
          // UTF-8 BOMがある場合は削除
          if (text.charCodeAt(0) === 0xfeff) {
            text = text.slice(1)
          }

          // デコード後に文字化けを検出
          // 置換文字(U+FFFD)の存在をチェック
          if (text.includes('\uFFFD')) {
            // 文字化けがある場合、エラーとする
            formik.setFieldTouched('employees', true, false)
            formik.setFieldError(
              'employees',
              'CSVファイルのエンコーディングをUTF-8にしてください',
            )
            return
          }
        } catch {
          // デコードに失敗した場合も文字化けとみなす
          formik.setFieldTouched('employees', true, false)
          formik.setFieldError(
            'employees',
            'CSVファイルのエンコーディングをUTF-8にしてください',
          )
          return
        }
        parse(text, {
          complete: (results: ParseResult<string[]>) => {
            const csvRowLength = 4
            // 最大行数を1000行に固定した。
            // レコード数に上限を設けておかないとmobile側での表示にも影響が出ると考えられるため一定ラインの上限は必要
            const csvRowMaxCount = 1000
            if (results.data.length > csvRowMaxCount) {
              // 最大行数を超えている場合、エラーとする
              formik.setFieldTouched('employees', true, false)
              formik.setFieldError(
                'employees',
                `csv取り込みの最大行数は${csvRowMaxCount}件までです`,
              )
              return
            }
            const notExistsPlaceNames = new Set()
            const uniqueEmployees: { [key: string]: Employee } = {}
            results.data
              .slice(1)
              .filter((row: string[]) => row.length === csvRowLength)
              .forEach((row: string[]) => {
                const employee = {
                  name: row[0],
                  nameKana: row[1],
                  employeeCode: row[2],
                  placeNodes: row[3]
                    .split(',')
                    .map(placeName => {
                      const placeNode = getPlaceNodesFromName(
                        placeName,
                        placeNodes,
                      )
                      if (placeNode === undefined) {
                        notExistsPlaceNames.add(placeName)
                      }
                      return {
                        uuid: placeNode?.uuid ?? '',
                        id: placeNode?.id ?? 0,
                        type: placeNode?.type ?? 0,
                        isRootNode: 0,
                        parentNodes: [],
                        nodes: [],
                      }
                    })
                    .filter(placeNode => placeNode.uuid !== ''),
                }
                if (employee.employeeCode === '') return
                uniqueEmployees[employee.employeeCode] = employee
              })
            const employeesList = Object.values(uniqueEmployees)
            if (employeesList.length === 0) {
              // 中身が空の場合、エラーとする
              formik.setFieldTouched('employees', true, false)
              formik.setFieldError(
                'employees',
                'CSVファイルに従業員が含まれていません',
              )
              return
            }
            formik.setFieldValue('employees', employeesList)
            if (notExistsPlaceNames.size !== 0) {
              const notExistsPlaceNamesString =
                Array.from(notExistsPlaceNames).join(', ')
              updatePlaceNodeWarning(
                `以下の現場は存在しません\n${notExistsPlaceNamesString}`,
              )
            }
          },
        })
      }
      reader.onerror = () => {
        // ファイルの読み込みに失敗した場合、エラーとする
        formik.setFieldTouched('employees', true, false)
        formik.setFieldError(
          'employees',
          'ファイルの読み込み中にエラーが発生しました',
        )
      }
      reader.readAsArrayBuffer(file)
    }
    e.target.value = ''
  }

  const getPlaceNodesFromName = (
    placeName: string,
    placeNodes: PlaceNode[],
  ) => {
    return placeNodes.find((placeNode: PlaceNode) => {
      if (placeNode.type === PlaceNodeTypeEnum.Place) {
        return placeNode.place?.name === placeName
      } else if (placeNode.type === PlaceNodeTypeEnum.PlaceGroup) {
        return placeNode.placeGroup?.name === placeName
      } else {
        return false
      }
    })
  }

  const sampleFileDownloadCSVData = [
    ['名前', '名前(カナ)', '従業員コード', '所属現場'],
    ['鈴木 太郎', 'スズキ タロウ', '0001', '神田店'],
    ['田中 次郎', 'タナカ ジロウ', '0002', '神田店'],
    ['佐藤 三郎', 'サトウ サブロウ', '0003', '渋谷店,大手町店'],
  ]

  const csvColumnsInfo = [
    { name: '名前', description: '(例)山田 太郎' },
    { name: '名前(カナ)', description: '(例)ヤマダ タロウ' },
    {
      name: '従業員コード',
      description: '貴社のフォーマットに合わせてご記入ください',
    },
    {
      name: '所属現場',
      description:
        '現場設定画面で登録されている現場の名前をカンマ区切り(,)で指定ください',
    },
  ]

  const inputEl = useRef<HTMLInputElement>(null)
  const { errors, values, handleSubmit, touched, resetForm } = formik
  return (
    <Stack
      spacing={4}
      py={4}
      px={3}
      sx={{ maxWidth: 900, width: '100%', mx: 'auto' }}
    >
      <Typography variant="subtitle1">
        従業員インポート用の CSV ファイルを選択してください。
      </Typography>
      {!isLoading && (
        <Alert
          severity="info"
          sx={{
            pr: 2,
            '& .MuiAlert-message': {
              width: '100%',
            },
          }}
        >
          <Stack spacing={1}>
            <Stack direction="row" spacing={1}>
              <Typography variant="subtitle1">
                CSV は以下のフォーマットにしてください。
              </Typography>
              <CSVLink
                data={sampleFileDownloadCSVData}
                uFEFF={true}
                filename="従業員サンプル.csv"
              >
                <Button
                  variant="outlined"
                  size="small"
                  color="primary"
                  startIcon={<FileDownload />}
                  sx={{ backgroundColor: 'white' }}
                >
                  サンプルダウンロード
                </Button>
              </CSVLink>
            </Stack>
            <Table
              size="small"
              sx={{ backgroundColor: 'white', width: 'auto' }}
            >
              <TableHead sx={{ backgroundColor: 'white' }}>
                <TableRow>
                  <TableCell size="small">カラム名</TableCell>
                  <TableCell size="small">説明</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {csvColumnsInfo.map(row => (
                  <TableRow key={row.name}>
                    <TableCell size="small" sx={{ width: '20%' }}>
                      {row.name}
                    </TableCell>
                    <TableCell size="small" sx={{ width: '80%' }}>
                      {row.description}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Stack>
        </Alert>
      )}
      <Stack spacing={1}>
        <Stack direction="row" spacing={1}>
          <Button
            variant="contained"
            color="secondary"
            onClick={() => {
              if (inputEl?.current) {
                inputEl.current.click()
              }
            }}
            startIcon={<FileUploadOutlined fontSize="small" />}
          >
            ファイルを選択
          </Button>
          <input
            type="file"
            style={{ display: 'none' }}
            ref={inputEl}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              parseEmployeesFromFile(e)
            }}
            accept=".csv"
          />
          {values.fileName !== '' && (
            <Stack direction="row" alignItems="center" spacing={1}>
              <AttachFileOutlined fontSize="small" />
              <Typography variant="body2">{values.fileName}</Typography>
              <IconButton
                size="small"
                onClick={() => {
                  resetForm()
                  updatePlaceNodeWarning('')
                }}
              >
                <Cancel fontSize="small" />
              </IconButton>
            </Stack>
          )}
        </Stack>
        {(Object.keys(errors || {}).length > 0 ||
          values.fileName ||
          placeNodeWarning) && (
          <Stack>
            {errors && (
              <FormikErrorMessage
                name="fileName"
                errors={errors}
                touched={touched}
              />
            )}
            {values.fileName && (
              <FormikErrorMessage
                name="employees"
                errors={errors}
                touched={touched}
              />
            )}
            {placeNodeWarning && (
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                {placeNodeWarning}
              </Typography>
            )}
          </Stack>
        )}
      </Stack>
      <Stack direction="row" spacing={1}>
        <Button
          variant="contained"
          color="primary"
          onClick={() => handleSubmit()}
          loading={isLoading}
          startIcon={<SystemUpdateAltOutlined fontSize="small" />}
          disabled={
            isLoading ||
            values.fileName === '' ||
            Object.keys(errors).length > 0
          }
        >
          CSVインポートを実行
        </Button>
      </Stack>
      {isLoading && <Loading />}
    </Stack>
  )
}

export default ImportEmployees
